import { useState, type ReactElement } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.js'
import { Button } from './ui/button.js'
import { cn } from '@/lib/utils.js'

export interface OverrideOption<T extends string | number> {
  label: string
  value: T
}

interface Props<T extends string | number> {
  open: boolean
  title: string
  options: OverrideOption<T>[]
  current: T
  allowFreeform?: boolean
  freeformPlaceholder?: string
  onSelect: (v: T) => void
  onClose: () => void
}

export const OverrideDialog = <T extends string | number>({
  open, title, options, current, allowFreeform, freeformPlaceholder, onSelect, onClose,
}: Props<T>): ReactElement => {
  const [freeform, setFreeform] = useState<string>('')

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <ul className="space-y-2">
          {options.map(opt => (
            <li key={String(opt.value)}>
              <button
                type="button"
                onClick={() => onSelect(opt.value)}
                className={cn(
                  'w-full text-left rounded-xl border px-4 py-3 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  opt.value === current
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-card hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>

        {allowFreeform && (
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={freeform}
              onChange={e => setFreeform(e.target.value)}
              placeholder={freeformPlaceholder ?? 'Custom value'}
              className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Button
              size="sm"
              onClick={() => {
                if (!freeform.trim()) return
                onSelect(freeform.trim() as T)
              }}
            >
              Use
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
